import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import AdminLayout from "@/components/admin/AdminLayout"
import { getAdminBanners, deleteAdminBanner } from "@/api/adminBannerApi"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"

type AdminBanner = {
    bannerId: number
    bannerType: string
    title: string
    imageUrl: string
    sortOrder: number
    isActive: string
    createdAt: string
}

export default function AdminBannerPage() {
    const navigate = useNavigate()
    const [banners, setBanners] = useState<AdminBanner[]>([])
    const [loading, setLoading] = useState(false)
    const [filterType, setFilterType] = useState<string>("") // ""(전체), "HERO", "PROMO"

    const fetchBanners = async () => {
        try {
            setLoading(true)
            const response = await getAdminBanners(filterType || undefined)
            setBanners(response.data || response)
        } catch (error) {
            console.error(error)
            alert("배너 목록을 불러오지 못했습니다.")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchBanners()
    }, [filterType])

    const handleDelete = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation() // 상세 페이지 이동 방지
        if (!confirm("정말 이 배너를 삭제하시겠습니까?")) return

        try {
            await deleteAdminBanner(id)
            alert("배너가 삭제되었습니다.")
            fetchBanners() // 목록 새로고침
        } catch (error) {
            console.error(error)
            alert("배너 삭제에 실패했습니다.")
        }
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                    <div>
                        <h2 className="text-3xl font-bold">배너 관리</h2>
                        <p className="mt-2 text-sm text-slate-500">메인 화면에 노출되는 배너와 프로모션을 관리합니다.</p>
                    </div>
                    <Button className="rounded-xl bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/admin/banners/new")}>
                        신규 배너 등록
                    </Button>
                </div>

                {/* 필터 탭 */}
                <div className="flex gap-2">
                    <Button variant={filterType === "" ? "default" : "outline"} onClick={() => setFilterType("")} className="rounded-xl">전체 보기</Button>
                    <Button variant={filterType === "HERO" ? "default" : "outline"} onClick={() => setFilterType("HERO")} className="rounded-xl">메인 상단 배너 (HERO)</Button>
                    <Button variant={filterType === "PROMO" ? "default" : "outline"} onClick={() => setFilterType("PROMO")} className="rounded-xl">하단 프로모션 (PROMO)</Button>
                </div>

                <Card className="rounded-[28px] border border-slate-200 shadow-sm">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px] text-sm text-left">
                                <thead>
                                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500">
                                    <th className="px-4 py-4 font-medium w-24">순서</th>
                                    <th className="px-4 py-4 font-medium w-32">타입</th>
                                    <th className="px-4 py-4 font-medium w-40">이미지</th>
                                    <th className="px-4 py-4 font-medium">타이틀</th>
                                    <th className="px-4 py-4 font-medium w-24">상태</th>
                                    <th className="px-4 py-4 font-medium w-24">관리</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">조회 중...</td></tr>
                                ) : banners.length === 0 ? (
                                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">등록된 배너가 없습니다.</td></tr>
                                ) : (
                                    banners.map((banner) => (
                                        <tr
                                            key={banner.bannerId}
                                            className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                                            onClick={() => navigate(`/admin/banners/${banner.bannerId}`)}
                                        >
                                            <td className="px-4 py-4 font-bold text-slate-900">{banner.sortOrder}</td>
                                            <td className="px-4 py-4">
                                                    <span className={`rounded-full px-3 py-1 text-xs font-medium ${banner.bannerType === 'HERO' ? 'bg-indigo-100 text-indigo-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                                        {banner.bannerType === 'HERO' ? '메인 상단' : '프로모션'}
                                                    </span>
                                            </td>
                                            <td className="px-4 py-4">
                                                <div className="h-12 w-20 overflow-hidden rounded-md border border-slate-200 bg-slate-100">
                                                    <img src={banner.imageUrl} alt="배너" className="h-full w-full object-cover" />
                                                </div>
                                            </td>
                                            <td className="px-4 py-4 font-medium text-slate-900">{banner.title}</td>
                                            <td className="px-4 py-4">
                                                {banner.isActive === 'Y'
                                                    ? <span className="text-blue-600 font-medium">노출중</span>
                                                    : <span className="text-slate-400">숨김</span>}
                                            </td>
                                            <td className="px-4 py-4">
                                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={(e) => handleDelete(e, banner.bannerId)}>
                                                    삭제
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    )
}